<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { Loader2, CheckCircle, AlertCircle } from 'lucide-vue-next'
import axios from 'axios'

const props = defineProps<{
  paymentStatusPath: string
}>()

const status = ref<'polling' | 'confirmed' | 'timeout' | 'error'>('polling')
const POLL_INTERVAL_MS = 2000
const MAX_POLLS = 15

let pollCount = 0
let timerId: ReturnType<typeof setInterval> | null = null

async function checkStatus() {
  try {
    const response = await axios.get(props.paymentStatusPath)
    const reservationStatus = response.data.status

    if (reservationStatus === 'confirmed') {
      status.value = 'confirmed'
      stopPolling()
      const Turbo = (window as unknown as { Turbo?: { visit: (url: string, options: { action: string }) => void } }).Turbo
      if (Turbo) {
        Turbo.visit(window.location.href.split('?')[0], { action: 'replace' })
      } else {
        window.location.href = window.location.href.split('?')[0]
      }
      return
    }

    if (reservationStatus === 'cancelled') {
      status.value = 'error'
      stopPolling()
      return
    }

    pollCount++
    if (pollCount >= MAX_POLLS) {
      status.value = 'timeout'
      stopPolling()
    }
  } catch {
    pollCount++
    if (pollCount >= MAX_POLLS) {
      status.value = 'timeout'
      stopPolling()
    }
  }
}

function stopPolling() {
  if (timerId) {
    clearInterval(timerId)
    timerId = null
  }
}

onMounted(() => {
  timerId = setInterval(checkStatus, POLL_INTERVAL_MS)
  checkStatus()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<template>
  <div class="retro-card p-6 mb-6">
    <div
      v-if="status === 'polling'"
      class="flex items-center gap-3"
    >
      <Loader2 class="w-6 h-6 text-lime-600 animate-spin" />
      <div>
        <p class="font-bold text-[#5a3a26]">
          決済を処理しています...
        </p>
        <p class="text-sm text-[#8b5a3b]">
          しばらくお待ちください。自動的に画面が更新されます。
        </p>
      </div>
    </div>

    <div
      v-else-if="status === 'confirmed'"
      class="flex items-center gap-3"
    >
      <CheckCircle class="w-6 h-6 text-green-600" />
      <p class="font-bold text-green-700">
        決済が完了しました。画面を更新しています...
      </p>
    </div>

    <div
      v-else-if="status === 'timeout'"
      class="flex items-center gap-3"
    >
      <AlertCircle class="w-6 h-6 text-amber-600" />
      <div>
        <p class="font-bold text-amber-800">
          決済処理に時間がかかっています
        </p>
        <p class="text-sm text-amber-700">
          ページを更新してご確認ください。
        </p>
      </div>
    </div>

    <div
      v-else-if="status === 'error'"
      class="flex items-center gap-3"
    >
      <AlertCircle class="w-6 h-6 text-red-600" />
      <p class="font-bold text-red-700">
        決済処理中にエラーが発生しました。管理者にお問い合わせください。
      </p>
    </div>
  </div>
</template>
